import React from "react";
import Pagination from "../../../components/Pagination";

/**
 * Mobile card view component for displaying products in a card layout
 * Used on smaller screens for better mobile experience
 */
const MobileCardView = ({
  productRows,
  isCompactView,
  handleNewPriceChange,
  handleRemoveOfferPrice,
  handleToggleVisibility,
  priceErrors,
  currencySymbol,
  page,
  totalPages,
  onPageChange,
}) => {
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto pb-4">
        {productRows.map((row, index) => {
          const errorKey = row.variantId
            ? `${row._id}-${row.variantId}`
            : row._id;
          const hasError = priceErrors[errorKey];

          return (
            <div
              key={`${row._id}-${row.variantId || index}`}
              className={`mb-4 rounded-lg shadow-md transition-shadow ${hasError ? "border-2 border-red-500" : "border border-gray-200"
                } bg-white`}
            >
              <div className="p-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-3">
                  {row.productName}
                </h3>

                {!isCompactView && (
                  <>
                    <div className="flex gap-4 mb-2 flex-wrap">
                      <p className="text-sm text-gray-600">
                        <strong>Category:</strong> {row.categoryName}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Subcategory:</strong> {row.subcategoryName}
                      </p>
                    </div>
                    <hr className="my-3 border-gray-200" />
                  </>
                )}

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Current Price</p>
                    <p className="text-xl font-semibold text-green-600">
                      {currencySymbol}
                      {row.price}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Offer Price</p>

                    {row.offerPrice ? (
                      <>
                        <p className="text-xl font-semibold text-red-600">
                          {currencySymbol}
                          {row.offerPrice}
                        </p>

                        {/* ✅ ADD REMOVE BUTTON HERE */}
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveOfferPrice(row._id, row.variantId)
                          }
                          className="mt-1 text-xs text-red-600 underline"
                        >
                          Remove Offer
                        </button>
                      </>
                    ) : (
                      <p className="text-xl font-semibold text-red-600"></p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-100 mb-3">
                  <span className="text-sm font-medium text-gray-700">Product Availability</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={row.isVisible || false}
                      onChange={() => handleToggleVisibility(row._id, row.variantId, row.isVisible || false)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-2 text-sm font-medium text-gray-900">
                      {row.isVisible ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                </div>

                <hr className="my-3 border-gray-200" />

                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Price
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-500 text-sm">
                        {currencySymbol}
                      </span>
                      <input
                        type="number"
                        value={row.newPrice}
                        onChange={(e) =>
                          handleNewPriceChange(
                            row._id,
                            row.variantId,
                            e.target.value,
                            "newPrice",
                          )
                        }
                        className="pl-8 pr-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Offer Price
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-500 text-sm">
                        {currencySymbol}
                      </span>
                      <input
                        type="number"
                        value={row.newOfferPrice}
                        onChange={(e) =>
                          handleNewPriceChange(
                            row._id,
                            row.variantId,
                            e.target.value,
                            "newOfferPrice",
                          )
                        }
                        className={`pl-8 pr-3 py-2 w-full border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasError
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                          }`}
                      />
                      {hasError && (
                        <p className="text-xs text-red-500 mt-1">
                          Offer price cannot be greater than price
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-gray-100 px-6 py-4 bg-white">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default MobileCardView;
